import { ref, reactive, computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import {
  generateCustomAttributeTypes,
  getDefaultConditions,
  getDefaultActions,
  generateCustomAttributes,
  KANBAN_EVENTS,
} from 'dashboard/helper/automationHelper';
import useAutomationValues from './useAutomationValues';

import {
  // AUTOMATION_RULE_EVENTS,
  // AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
  DEFAULT_SCHEDULED_MESSAGE_DELAY_MINUTES,
} from 'dashboard/routes/dashboard/settings/automation/constants.js';
import { CUSTOM_ATTRIBUTE_EVENTS } from 'dashboard/constants/automation';

/**
 * Composable for handling automation-related functionality.
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export function useAutomation(startValue = null) {
  const getters = useStoreGetters();
  const { t } = useI18n();

  const {
    booleanFilterOptions,
    statusFilterOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
  } = useAutomationValues();

  const automation = ref(startValue);
  const automationTypes = reactive(structuredClone(AUTOMATIONS));
  const eventName = computed(() => automation.value?.event_name);

  /**
   * Handles the event change for an automation.value.
   */
  const onEventChange = () => {
    automation.value.conditions = getDefaultConditions(eventName.value);
    automation.value.actions = getDefaultActions();
  };

  /**
   * Appends a new condition to the automation.value.
   */
  const appendNewCondition = () => {
    let defaultCondition = getDefaultConditions(eventName.value);

    const isKanbanEvent = KANBAN_EVENTS.includes(eventName.value);
    if (isKanbanEvent) {
      const hasBoardCondition = automation.value.conditions.some(
        c => c.attribute_key === 'kanban_board_id'
      );
      if (hasBoardCondition) {
        defaultCondition = [
          {
            attribute_key: 'kanban_step_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
            custom_attribute_type: '',
          },
        ];
      }
    }

    automation.value.conditions = [
      ...automation.value.conditions,
      ...defaultCondition,
    ];
  };

  /**
   * Appends a new action to the automation.value.
   */
  const appendNewAction = () => {
    const defaultAction = getDefaultActions();
    automation.value.actions = [...automation.value.actions, ...defaultAction];
  };

  /**
   * Removes a filter from the automation.value.
   * @param {number} index - The index of the filter to remove.
   */
  const removeFilter = index => {
    if (automation.value.conditions.length <= 1) {
      useAlert(t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
    } else {
      automation.value.conditions = automation.value.conditions.filter(
        (_, i) => i !== index
      );
    }
  };

  /**
   * Removes an action from the automation.value.
   * @param {number} index - The index of the action to remove.
   */
  const removeAction = index => {
    if (automation.value.actions.length <= 1) {
      useAlert(t('AUTOMATION.ACTION.DELETE_MESSAGE'));
    } else {
      automation.value.actions = automation.value.actions.filter(
        (_, i) => i !== index
      );
    }
  };

  /**
   * Resets a filter in the automation.value.
   * @param {Object} automationTypes - The automation types object.
   * @param {number} index - The index of the filter to reset.
   * @param {Object} currentCondition - The current condition object.
   */
  const resetFilter = (index, currentCondition) => {
    const newConditions = [...automation.value.conditions];

    newConditions[index] = {
      ...newConditions[index],
      filter_operator: automationTypes[eventName.value].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value,
      values: '',
    };

    automation.value.conditions = newConditions;
  };

  /**
   * Resets an action in the automation.value.
   * @param {number} index - The index of the action to reset.
   */
  const resetAction = index => {
    const action = automation.value.actions[index];
    const newActions = [...automation.value.actions];

    // Initialize params per action: scheduled messages need a default delay,
    // relative-date setters default to 0 (now).
    let actionParams = [];
    if (action.action_name === 'create_scheduled_message') {
      actionParams = [
        { delay_minutes: DEFAULT_SCHEDULED_MESSAGE_DELAY_MINUTES },
      ];
    } else if (
      ['set_due_date', 'set_start_date'].includes(action.action_name)
    ) {
      actionParams = [{ offset_minutes: 0 }];
    } else if (action.action_name === 'set_custom_attribute') {
      actionParams = [{ attribute_key: '', attribute_model: '', value: '' }];
    } else if (action.action_name === 'add_product_to_task') {
      actionParams = [{ product_id: null, quantity: 1 }];
    }

    newActions[index] = {
      ...newActions[index],
      action_params: actionParams,
    };

    automation.value.actions = newActions;
  };

  /**
   * This function formats the custom attributes for automation types.
   * It retrieves custom attributes for conversations and contacts,
   * generates custom attribute types, and adds them to the relevant automation types.
   */
  const manifestCustomAttributes = () => {
    const conversationCustomAttributesRaw = getters[
      'attributes/getAttributesByModel'
    ].value('conversation_attribute');
    const contactCustomAttributesRaw =
      getters['attributes/getAttributesByModel'].value('contact_attribute');

    const conversationCustomAttributeTypes = generateCustomAttributeTypes(
      conversationCustomAttributesRaw,
      'conversation_attribute'
    );
    const contactCustomAttributeTypes = generateCustomAttributeTypes(
      contactCustomAttributesRaw,
      'contact_attribute'
    );

    const manifestedCustomAttributes = generateCustomAttributes(
      conversationCustomAttributeTypes,
      contactCustomAttributeTypes,
      t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
      t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
    );

    const CUSTOM_ATTR_HEADER_KEYS = new Set([
      'conversation_custom_attribute',
      'contact_custom_attribute',
    ]);

    // The list is named in `dashboard/constants/automation`, not spelled out here. It used to name
    // `message_created` alone and reached the edit trigger only because the two shared one object,
    // so anything that replaced `automationTypes.message_created` detached the edit trigger from its
    // custom attributes without a word. #667
    CUSTOM_ATTRIBUTE_EVENTS.forEach(eventToUpdate => {
      const standardConditions = automationTypes[
        eventToUpdate
      ].conditions.filter(
        c => !c.customAttributeType && !CUSTOM_ATTR_HEADER_KEYS.has(c.key)
      );
      automationTypes[eventToUpdate].conditions = [
        ...standardConditions,
        ...manifestedCustomAttributes,
      ];
    });

    // Kanban (task) events get their own task_attribute custom attributes.
    const taskCustomAttributesRaw =
      getters['attributes/getAttributesByModel'].value('task_attribute');
    const taskCustomAttributeTypes = generateCustomAttributeTypes(
      taskCustomAttributesRaw,
      'task_attribute'
    );
    const manifestedTaskCustomAttributes = taskCustomAttributeTypes.length
      ? [
          {
            key: 'task_custom_attribute',
            name: t('AUTOMATION.CONDITION.TASK_CUSTOM_ATTR_LABEL'),
            disabled: true,
          },
          ...taskCustomAttributeTypes,
        ]
      : [];

    const TASK_CUSTOM_ATTR_HEADER_KEYS = new Set(['task_custom_attribute']);
    KANBAN_EVENTS.forEach(eventToUpdate => {
      const standardConditions = automationTypes[
        eventToUpdate
      ].conditions.filter(
        c => !c.customAttributeType && !TASK_CUSTOM_ATTR_HEADER_KEYS.has(c.key)
      );
      automationTypes[eventToUpdate].conditions = [
        ...standardConditions,
        ...manifestedTaskCustomAttributes,
      ];
    });
  };

  return {
    automation,
    automationTypes,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
    booleanFilterOptions,
    statusFilterOptions,
    onEventChange,
    getConditionDropdownValues,
    appendNewCondition,
    appendNewAction,
    removeFilter,
    removeAction,
    resetFilter,
    resetAction,
    getActionDropdownValues,
    manifestCustomAttributes,
  };
}
